"use client";

import { useSelector, useDispatch } from "react-redux";
import { useEffect, useCallback } from "react";
import {
  loginUser,
  signupUser,
  logoutUser,
  logout,
  clearError,
  clearMessage,
  checkAuthStatus,
  forgotPassword,
  adminLogin,
  adminLoginVerify,
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectIsSeller,
  selectAuthLoading,
  selectAuthError,
  selectAuthMessage,
} from "../store/slices/authSlice";
import { selectAuthInitialized } from "../store/slices/authInitSlice";

export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isSeller = useSelector(selectIsSeller);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const message = useSelector(selectAuthMessage);
  const initialized = useSelector(selectAuthInitialized);

  // Memoize the auth check to prevent unnecessary calls
  const checkAuth = useCallback(() => {
    if (!isAuthenticated && !user && !loading) {
      dispatch(checkAuthStatus());
    }
  }, [dispatch, isAuthenticated, user, loading]);

  // Check auth status on hook initialization - only once
  useEffect(() => {
    checkAuth();
  }, []); // Remove dependencies to run only once

  const login = useCallback(
    async (email, password) => {
      try {
        const result = await dispatch(loginUser({ email, password })).unwrap();
        return {
          success: true,
          user: result.user,
          message: "Login successful",
        };
      } catch (error) {
        console.error("Login error in useAuth:", error);
        return {
          success: false,
          error:
            typeof error === "string"
              ? error
              : "Login failed. Please try again.",
        };
      }
    },
    [dispatch]
  );

  const signup = useCallback(
    async (email, password, full_name, role = "buyer") => {
      try {
        const result = await dispatch(
          signupUser({ email, password, full_name, role })
        ).unwrap();

        return {
          success: true,
          user: result.user,
          message: "Registration successful",
        };
      } catch (error) {
        console.error("Signup error in useAuth:", error);
        return {
          success: false,
          error:
            typeof error === "string"
              ? error
              : "Registration failed. Please try again.",
        };
      }
    },
    [dispatch]
  );

  // Admin login step 1 - send OTP
  const adminLoginStep1 = useCallback(
    async (email, password) => {
      try {
        const result = await dispatch(adminLogin({ email, password })).unwrap();
        return {
          success: true,
          message: result.message || "Access code sent to your email",
        };
      } catch (error) {
        console.error("Admin login error in useAuth:", error);
        return {
          success: false,
          error:
            typeof error === "string"
              ? error
              : "Admin login failed. Please try again.",
        };
      }
    },
    [dispatch]
  );

  // Admin login step 2 - verify OTP and complete login
  const adminLoginStep2 = useCallback(
    async (email, otp) => {
      try {
        const result = await dispatch(
          adminLoginVerify({ email, otp })
        ).unwrap();
        return {
          success: true,
          user: result.user,
          message: "Admin login successful",
        };
      } catch (error) {
        console.error("Admin OTP verification error in useAuth:", error);
        return {
          success: false,
          error:
            typeof error === "string"
              ? error
              : "Invalid access code. Please try again.",
        };
      }
    },
    [dispatch]
  );

  const logoutUserAction = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      return { success: true, message: "Logged out successfully" };
    } catch (error) {
      // Even if logout API fails, we still clear local state
      dispatch(logout());
      return { success: true, message: "Logged out successfully" };
    }
  }, [dispatch]);

  const sendPasswordReset = useCallback(
    async (email) => {
      try {
        const result = await dispatch(forgotPassword({ email })).unwrap();
        return { success: true, message: result };
      } catch (error) {
        return { success: false, error };
      }
    },
    [dispatch]
  );

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearAuthMessage = useCallback(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const isAdmin = useCallback(() => {
    return user?.role === "admin";
  }, [user]);

  return {
    // State
    user,
    isAuthenticated,
    isSeller,
    isAdmin,
    loading,
    error,
    message,
    auth,
    initialized,
    // Actions
    login,
    signup,
    adminLoginStep1,
    adminLoginStep2,
    logout: logoutUserAction,
    sendPasswordReset,
    clearError: clearAuthError,
    clearMessage: clearAuthMessage,
    // Helper functions
    isAuthenticatedUser: () => isAuthenticated,
    isSellerUser: () => isSeller,
    isAdminUser: () => isAdmin,
    getUserId: () => user?.id || user?._id,
    getUserEmail: () => user?.email,
    getUserName: () => user?.full_name || user?.name,
    getUserRole: () => user?.role,
  };
};
